import { LOG } from "./LoggerApi";
import { AuthContext } from "../Auth/AuthContext";
import { useCallback, useContext, useRef } from "react";
import { useMutationWrapper } from "@SeemplicityDev/shared";
import * as Sentry from "@sentry/react";
import type { ApolloError } from "@apollo/client";
import type { AuthState } from "../types/AuthProvider";

export const LOG_SEVERITY = {
  CRITICAL: 0, // User-facing feature failures (e.g. details pane can't load)
  DEFAULT: 1, // Background/infrastructure errors
} as const;

type LogSeverity = (typeof LOG_SEVERITY)[keyof typeof LOG_SEVERITY];

const logLevels = {
  DEBUG: "DEBUG",
  INFO: "INFO",
  WARNING: "WARNING",
  ERROR: "ERROR",
  CRITICAL: "CRITICAL",
} as const;

export const FILTERS_DATA_QUERY_ERROR =
  "Filter metadata: getFiltersData query error";

export const SCOPES_QUERY_ERROR = "Scope hierarchy: GET_SCOPES query error";
export const SCOPES_HIERARCHY_UNUSABLE =
  "Scope hierarchy: response contained no usable scope tree";

const ERRORS_TO_DEBOUNCE = [
  "Error Boundary: Unable to login, please try again in a few moments.",
  FILTERS_DATA_QUERY_ERROR,
  SCOPES_QUERY_ERROR,
  SCOPES_HIERARCHY_UNUSABLE,
];

// For callers that log a synthetic Error so the raw ApolloError's own properties
// (server-supplied messages and extensions) never reach Sentry. `graphql` keeps
// the 401 demotion in logError working, since a synthetic Error carries no
// networkError of its own.
export const scrubbedApolloErrorExtra = (error: ApolloError) => ({
  graphQLErrors: (error.graphQLErrors ?? []).map(({ path, extensions }) => ({
    path,
    code: extensions?.code,
  })),
  graphql: {
    statusCode: (error.networkError as NetworkError | null)?.statusCode,
  },
});

const ERROR_DEBOUNCE_TIME = 2000;
const debounceTimers: Record<string, ReturnType<typeof setTimeout>> = {};

type NetworkError = Error & { statusCode?: number };

// Covers plain Error, ApolloError (with networkError), and ServerError (with statusCode)
type LoggableError = Error & {
  statusCode?: number;
  networkError?: NetworkError | null;
};

// Mirrors Sentry's Primitive type without importing from @sentry/types
type Primitive = number | string | boolean | bigint | symbol | null | undefined;

interface GraphqlNetworkError {
  statusCode?: number;
}

interface LogErrorExtra extends Record<string, any> {
  graphql?: GraphqlNetworkError;
}

export interface LogErrorInfo {
  severity?: LogSeverity;
  tags?: Record<string, Primitive>;
  extra?: LogErrorExtra;
}

interface ErrorVariables {
  msg: string;
  additional_info: string;
  username: string;
}

export interface UseLoggerReturnValue {
  logError: (rawError: unknown, message: string, info?: LogErrorInfo) => void;
  logWarning: (rawError: unknown, message: string, info?: LogErrorInfo) => void;
  logInfo: (message: string) => void;
}

const useLogger = (): UseLoggerReturnValue => {
  const { authState } = useContext(AuthContext) as { authState: AuthState };
  const [log] = useMutationWrapper(LOG);
  const usernameRef = useRef<string>();
  usernameRef.current = authState?.username ?? "";

  const getErrorVariables = useCallback(
    (
      error: LoggableError | null,
      message: string,
      info: LogErrorInfo
    ): ErrorVariables => {
      let nonEnumerableErrorObject: Record<string, any> = {};

      if (error) {
        const errorRecord = error as LoggableError & Record<string, any>;
        nonEnumerableErrorObject = Object.getOwnPropertyNames(error).reduce<
          Record<string, any>
        >((acc, key) => ({ ...acc, [`error_${key}`]: errorRecord[key] }), {
          error_name: error.name,
        });
      }

      return {
        msg: message,
        additional_info: JSON.stringify({
          ...info,
          ...nonEnumerableErrorObject,
        }),
        username: usernameRef.current ?? "",
      };
    },
    [usernameRef]
  );

  const logWarning = useCallback(
    (rawError: unknown, message: string, info?: LogErrorInfo): void => {
      if (window.location.hostname !== "localhost") {
        log({
          variables: {
            levelname: logLevels.WARNING,
            ...getErrorVariables(
              rawError as LoggableError | null,
              message,
              info ?? {}
            ),
          },
        }).catch(() => {});
      }
    },
    [getErrorVariables, log]
  );

  const logError = useCallback(
    (rawError: unknown, message: string, info: LogErrorInfo = {}): void => {
      if (!info || typeof info !== "object" || Array.isArray(info)) {
        info = {};
      }

      const error = rawError as LoggableError | null;

      // 401 = token expired — expected behaviour, degrade to warning and skip Sentry
      if (
        error?.statusCode === 401 ||
        error?.networkError?.statusCode === 401 ||
        info.extra?.graphql?.statusCode === 401
      ) {
        logWarning(rawError, message, info);
        return;
      }

      const { severity = LOG_SEVERITY.DEFAULT, ...restInfo } = info;
      const level: Sentry.SeverityLevel =
        severity === LOG_SEVERITY.CRITICAL ? "fatal" : "error";

      if (window.location.hostname !== "localhost") {
        const { additional_info } = getErrorVariables(error, message, restInfo);

        const captureContext: Parameters<typeof Sentry.captureException>[1] = {
          ...restInfo,
          level,
          extra: {
            ...restInfo.extra,
            additional_info,
            severity,
          },
        };

        if (ERRORS_TO_DEBOUNCE.includes(message)) {
          if (debounceTimers[message]) {
            clearTimeout(debounceTimers[message]);
          }
          debounceTimers[message] = setTimeout(() => {
            Sentry.captureException(rawError, captureContext);
          }, ERROR_DEBOUNCE_TIME);
        } else {
          Sentry.captureException(rawError, captureContext);
        }
      }
    },
    [getErrorVariables, logWarning]
  );

  const logInfo = useCallback(
    (message: string): void => {
      if (window.location.hostname !== "localhost") {
        log({
          variables: {
            levelname: logLevels.INFO,
            msg: message,
            username: usernameRef.current ?? "",
          },
        }).catch(() => {});
      }
    },
    [usernameRef, log]
  );

  return { logError, logInfo, logWarning };
};

export default useLogger;
